// As rodas no campus: onde ficam as mesas de cada roda (TV, videogame, fliperama, pingue-pongue, papo,
// corredor, jokenpô, espelho), quem entra em qual (só ociosos, pelo traço de cada um), o placar e os
// balões de cada momento, e o que se desenha por cima dos móveis.
//
// As regras puras estão em src/core/rodas.ts. Aqui moram as coordenadas das salas e o ciclo:
//
//   begin() → offer(ocioso)… → commit()   a cada quadro, de graça se nada mudou (assinatura numérica)
//   tick()                                 a cada 200 ms: placar, balões, poses, sons
//   coleta()                               o que desenhar nos andares visíveis
//
// Quem volta a trabalhar deixa de ser oferecido: o commit seguinte o tira da roda na hora.

import { CAST_BY_ID } from '../../core/cast.js';
import { stableHash } from '../../core/hash.js';
import {
  GESTOS,
  JOKENPO_ROUND_MS,
  PING_POINT_MS,
  duracaoDaRoda,
  jokenpo as jokenpoDe,
  lanceDaTv,
  melhorDe3,
  pingPong as pingPongDe,
  planejaRodas,
  pontosFliperama,
  programaDaTv,
  sorteio,
  tracoDe,
  vezDeFalar,
  type Candidato,
  type JokenpoEstado,
  type Mesa,
  type MelhorDe3,
  type PingEstado,
  type Programa,
  type RodaKind,
  type Sessao,
  type Traco,
} from '../../core/rodas.js';
import type { Agent, SocialProfile } from '../../core/types.js';
import { TILE, type Dir, type SpotKind, type SpotUse } from '../../core/world.js';
import { FLIPERAMA_TELA, TV_TELA, desenhaBola, desenhaBrilho, desenhaFliperamaTela, desenhaMoeda, desenhaPipoca, desenhaTv } from '../art/rodas.js';
import type { Pose } from '../engine/character.js';
import type { Drawable } from '../engine/scene.js';
import { t, type Chave } from '../i18n.js';
import type { WorldSpot } from './behavior.js';
import type { CampusFloor, PlacedMap } from './layout.js';

/** Uma mesa de roda com os lugares em coordenadas do mundo e o canto do móvel principal (px de arte). */
export interface MesaWeb extends Mesa {
  spots: WorldSpot[];
  room: string;
  px: { x: number; y: number };
}

export const RODA_EMOJI: Readonly<Record<RodaKind, string>> = { tv: '📺', videogame: '🎮', fliperama: '🕹️', pingpong: '🏓', papo: '☕', corredor: '💬', jokenpo: '✊', espelho: '🪞' };

const SLOT_MS = 90_000;
/** Quem não chega ao lugar da roda neste prazo (o prédio é grande) desiste e a roda se desfaz. */
const PRAZO_CHEGADA_MS = 90_000;
const TICK_MS = 200;
/** Pulso de braço levantado: quanto dura o "raise" a cada ponto do pingue-pongue. */
const BATIDA_MS = 350;
/** Duplas de conversa em pé por corredor (espalhadas): o corredor de uma sede é comprido. */
const MAX_DUPLAS = 4;
const EMOJIS_CORREDOR = ['💬', '😄', '🤔', '👀', '🙂'] as const;
const EMOJIS_ESPELHO = ['✨', '💄', '😎', '🪞', '💇'] as const;

// ------------------------------------------------------------------ mesas de cada andar

function ponto(placed: PlacedMap, id: string, x: number, y: number, facing: Dir, kind: SpotKind = 'standing', use: SpotUse = 'rest'): WorldSpot {
  return { id: `${placed.id}#${id}`, x: placed.x + x, y: placed.y + y, facing, kind, use, room: placed.id, area: placed.map.kind };
}

function mesa(placed: PlacedMap, id: string, kind: RodaKind, min: number, spots: WorldSpot[], px: { x: number; y: number }): MesaWeb {
  return { id: `${placed.id}#${id}`, kind, seats: spots.length, min, spots, room: placed.id, px };
}

/** Lugares de cada roda no andar: lounge, copa, banheiros e corredor (o que o andar não tem, fica de fora). */
export function mesasDoAndar(floor: CampusFloor): MesaWeb[] {
  const out: MesaWeb[] = [];
  for (const p of floor.rooms) {
    const ox = p.x * TILE;
    const oy = p.y * TILE;
    if (p.map.id === 'lounge') {
      // TV no rack (13,8): quem assiste fica embaixo, de frente; os jogadores sentam nas cadeiras gamer
      const tv = { x: ox + 13 * TILE + TV_TELA.dx, y: oy + 9 * TILE - 28 + TV_TELA.dy };
      out.push(mesa(p, 'tv', 'tv', 1, [ponto(p, 'tv-0', 12, 9, 'up'), ponto(p, 'tv-1', 13, 9, 'up'), ponto(p, 'tv-2', 14, 9, 'up')], tv));
      out.push(mesa(p, 'videogame', 'videogame', 2, [ponto(p, 'lounge:gamer-0', 13, 7, 'down', 'chair'), ponto(p, 'lounge:gamer-1', 14, 7, 'down', 'chair')], tv));
      out.push(mesa(p, 'fliperama', 'fliperama', 1, [ponto(p, 'fliperama-0', 13, 2, 'up')], { x: ox + 13 * TILE + FLIPERAMA_TELA.dx, y: oy + 2 * TILE - 30 + FLIPERAMA_TELA.dy }));
      out.push(mesa(p, 'pingpong', 'pingpong', 2, [ponto(p, 'lounge:pingpong-0', 8, 3, 'right'), ponto(p, 'lounge:pingpong-1', 12, 4, 'left')], { x: ox + 9 * TILE, y: oy + 5 * TILE - 28 }));
      out.push(mesa(p, 'jokenpo', 'jokenpo', 2, [ponto(p, 'jokenpo-0', 4, 8, 'right'), ponto(p, 'jokenpo-1', 6, 8, 'left')], { x: ox + 5 * TILE, y: oy + 8 * TILE }));
    } else if (p.map.id === 'copa') {
      // mesa de café: quatro de frente uns para os outros, e a ponta com mais dois
      const cadeira = (n: number, x: number, y: number, facing: Dir) => ponto(p, `copa:cafe-${n}`, x, y, facing, 'chair', 'eat');
      out.push(mesa(p, 'papo-0', 'papo', 2, [cadeira(0, 5, 4, 'down'), cadeira(3, 5, 6, 'up'), cadeira(1, 6, 4, 'down'), cadeira(4, 6, 6, 'up')], { x: ox + 5 * TILE, y: oy + 5 * TILE }));
      out.push(mesa(p, 'papo-1', 'papo', 2, [cadeira(2, 7, 4, 'down'), cadeira(5, 7, 6, 'up')], { x: ox + 7 * TILE, y: oy + 5 * TILE }));
    } else if (p.map.id === 'banheiros') {
      // uma pessoa por pia, de frente para o espelho
      [2, 4, 9, 11].forEach((x, i) => out.push(mesa(p, `espelho-${i}`, 'espelho', 1, [ponto(p, `espelho-${i}`, x, 2, 'up')], { x: ox + x * TILE, y: oy - 14 })));
    }
  }
  // corredor: duplas conversando em pé, na fileira de baixo (as de cima ficam livres para quem passa)
  const c = floor.corridor;
  const duplas = Math.max(1, Math.min(MAX_DUPLAS, Math.floor((c.w - 6) / 8)));
  const passo = Math.floor((c.w - 6) / duplas);
  for (let i = 0, x = 3; i < duplas; i++, x += passo) {
    out.push(mesa(c, `conversa-${i}`, 'corredor', 2, [ponto(c, `conversa-${i}-0`, x, 2, 'right'), ponto(c, `conversa-${i}-1`, x + 1, 2, 'left')], { x: c.x * TILE + x * TILE, y: c.y * TILE }));
  }
  return out;
}

// ------------------------------------------------------------------ rodas vivas

interface Viva {
  floorKey: string;
  mesa: MesaWeb;
  seed: string;
  members: string[];
  /** Fim da roda. Até todos chegarem é o prazo de chegada; ao começar vira a duração da roda (60 a 120 s). */
  endsAt: number;
  /** Quando todos chegaram aos lugares: o relógio da roda começa aqui. */
  startedAt?: number;
  programa: Programa;
  lance: boolean;
  ping?: PingEstado;
  joken?: JokenpoEstado;
  jogo?: MelhorDe3;
  lastPoints: number;
  lastFase: string;
  lastSound: number;
  fx: Drawable[];
}

/** O que o campus entrega ao tick. */
export interface ContextoRodas {
  /** Já chegou ao lugar da roda? */
  chegou(id: string, spot: WorldSpot): boolean;
  /** A roda está na tela e vale tocar o som dela? */
  audivel(floorKey: string, x: number, y: number): boolean;
  toca(roda: 'pingue-pongue' | 'fliperama' | 'jokenpo'): void;
  /** epoch ms (a programação da TV segue o relógio). */
  epoch: number;
}

export interface PisoDeRodas {
  floor: CampusFloor;
}

interface Oferta {
  id: string;
  look: string;
  floorKey: string;
}

const hashes = new Map<string, number>();
function hashDe(id: string): number {
  let h = hashes.get(id);
  if (h === undefined) {
    h = stableHash(id);
    hashes.set(id, h);
  }
  return h;
}

/** Texto curto de pontos com separador de milhar: 12.400. */
function milhar(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

export class Rodas {
  private sessoes: Viva[] = [];
  private readonly porAgente = new Map<string, { s: Viva; i: number }>();
  private readonly mesasPorAndar = new Map<string, MesaWeb[]>();
  private readonly poses = new Map<string, Pose>();
  private readonly falas = new Map<string, string>();
  private readonly textos = new Map<string, string>();
  private readonly tvJogo = new Set<string>();
  private ofertas: Oferta[] = [];
  private usadas = 0;
  private sig = 0;
  private ultimaSig = -1;
  private proxima = 0;
  private ultimoTick = -Infinity;
  /** Movimento reduzido: nada de animação rápida (bola, moeda voando, mãos, telas). */
  reduzido = false;
  private nomes: Array<{ id: string; nome: string }> = [];

  constructor() {
    atual = this;
  }

  /** Limpa tudo (o layout mudou: as mesas e os lugares são outros). */
  reset(): void {
    this.sessoes = [];
    this.porAgente.clear();
    this.mesasPorAndar.clear();
    this.poses.clear();
    this.falas.clear();
    this.textos.clear();
    this.ultimaSig = -1;
  }

  // ---------------------------------------------------------------- ciclo por quadro

  begin(): void {
    this.usadas = 0;
    this.sig = 0;
  }

  /** Oferece um ocioso. Subagente e quem não está ocioso nem devem ser oferecidos. */
  offer(agent: Agent, floorKey: string): void {
    if (agent.status !== 'idle' || agent.parentId) return;
    this.sig = (this.sig ^ Math.imul(hashDe(agent.id) ^ hashDe(floorKey), 0x9e3779b1)) | 0;
    const o = this.ofertas[this.usadas];
    if (o) {
      o.id = agent.id;
      o.look = agent.look;
      o.floorKey = floorKey;
    } else this.ofertas[this.usadas] = { id: agent.id, look: agent.look, floorKey };
    this.usadas++;
  }

  /** Refaz as rodas se quem está ocioso mudou ou alguma roda acabou. */
  commit(now: number, andares: ReadonlyMap<string, PisoDeRodas>, social: Readonly<Record<string, SocialProfile>>, nomes: () => Array<{ id: string; nome: string }>): void {
    const sig = (this.sig ^ this.usadas) | 0;
    if (sig === this.ultimaSig && now < this.proxima) return;
    this.ultimaSig = sig;
    const livres = new Map<string, Oferta[]>();
    const presentes = new Set<string>();
    for (let i = 0; i < this.usadas; i++) {
      const o = this.ofertas[i]!;
      presentes.add(`${o.floorKey}|${o.id}`);
      let lista = livres.get(o.floorKey);
      if (!lista) livres.set(o.floorKey, (lista = []));
      lista.push(o);
    }
    // quem continua: roda que não acabou e ainda tem gente suficiente (sai quem deixou de estar ocioso)
    const mantidas: Viva[] = [];
    const ocupadas = new Set<string>();
    for (const s of this.sessoes) {
      if (now >= s.endsAt || !andares.has(s.floorKey)) continue;
      const members = s.members.filter((id) => presentes.has(`${s.floorKey}|${id}`));
      if (members.length < s.mesa.min) continue;
      if (members.length !== s.members.length) {
        s.members = members;
        s.startedAt = undefined;
        s.endsAt = now + PRAZO_CHEGADA_MS;
      }
      mantidas.push(s);
      ocupadas.add(s.mesa.id);
      for (const id of members) presentes.delete(`${s.floorKey}|${id}`);
    }
    // quem sobrou forma rodas novas, andar por andar
    const slot = Math.floor(now / SLOT_MS);
    this.nomes = nomes();
    for (const [floorKey, lista] of livres) {
      const piso = andares.get(floorKey);
      if (!piso) continue;
      const cands: Candidato[] = [];
      const idsDoAndar = new Map(lista.map((o) => [o.look, o.id]));
      for (const o of lista) {
        if (!presentes.has(`${floorKey}|${o.id}`)) continue;
        const amigos = social[o.look]?.friends;
        const friends = amigos?.length ? new Set(amigos.flatMap((f) => idsDoAndar.get(f.castId) ?? [])) : undefined;
        cands.push({ id: o.id, traco: tracoDe(o.id), ...(friends ? { friends } : {}) });
      }
      if (!cands.length) continue;
      const todas = this.mesasDe(floorKey, piso.floor);
      const mesas = todas.filter((m) => !ocupadas.has(m.id));
      const porId = new Map(mesas.map((m) => [m.id, m]));
      for (const sessao of planejaRodas(mesas, cands, slot)) mantidas.push(this.nova(floorKey, porId.get(sessao.mesa.id)!, sessao, now));
    }
    this.sessoes = mantidas;
    this.porAgente.clear();
    let proxima = Infinity;
    for (const s of mantidas) {
      s.members.forEach((id, i) => this.porAgente.set(id, { s, i }));
      proxima = Math.min(proxima, s.endsAt);
    }
    this.proxima = proxima;
  }

  private mesasDe(floorKey: string, floor: CampusFloor): MesaWeb[] {
    let m = this.mesasPorAndar.get(floorKey);
    if (!m) this.mesasPorAndar.set(floorKey, (m = mesasDoAndar(floor)));
    return m;
  }

  private nova(floorKey: string, m: MesaWeb, sessao: Sessao, now: number): Viva {
    const s: Viva = { floorKey, mesa: m, seed: sessao.seed, members: sessao.members, endsAt: now + PRAZO_CHEGADA_MS, programa: 'futebol', lance: false, lastPoints: 0, lastFase: '', lastSound: 0, fx: [] };
    s.fx = this.efeitos(s);
    return s;
  }

  // ---------------------------------------------------------------- consulta (por agente)

  /** O lugar da roda do agente (undefined = não está em nenhuma). */
  spotOf(id: string): WorldSpot | undefined {
    const a = this.porAgente.get(id);
    return a?.s.mesa.spots[a.i];
  }

  poseOf(id: string): Pose | undefined {
    return this.porAgente.has(id) ? this.poses.get(id) : undefined;
  }

  falaOf(id: string): string | undefined {
    return this.porAgente.has(id) ? this.falas.get(id) : undefined;
  }

  /** "🏓 Pingue-pongue · 7×5": o que o agente faz agora, para o painel. Só quando a roda já começou. */
  textoOf(id: string): string | undefined {
    return this.porAgente.has(id) ? this.textos.get(id) : undefined;
  }

  /** A roda (tipo) do agente, mesmo antes de a roda começar. */
  rodaOf(id: string): RodaKind | undefined {
    return this.porAgente.get(id)?.s.mesa.kind;
  }

  /** Quantas rodas estão rolando (para testes e depuração). */
  total(): number {
    return this.sessoes.length;
  }

  // ---------------------------------------------------------------- relógio das rodas

  tick(now: number, ctx: ContextoRodas): void {
    if (now - this.ultimoTick < TICK_MS) return;
    this.ultimoTick = now;
    this.poses.clear();
    this.falas.clear();
    this.textos.clear();
    this.tvJogo.clear();
    for (const s of this.sessoes) {
      if (s.startedAt === undefined) {
        let todos = true;
        for (let i = 0; i < s.members.length; i++) if (!ctx.chegou(s.members[i]!, s.mesa.spots[i]!)) todos = false;
        if (!todos) continue;
        s.startedAt = now;
        s.endsAt = now + duracaoDaRoda(s.seed);
      }
      this.anima(s, now - s.startedAt, now, ctx);
    }
  }

  private anima(s: Viva, e: number, now: number, ctx: ContextoRodas): void {
    const k = s.mesa.kind;
    const m = s.members;
    const rotulo = `${RODA_EMOJI[k]} ${t(`roda.${k}` as Chave)}`;
    const spot0 = s.mesa.spots[0]!;
    const audivel = ctx.audivel(s.floorKey, spot0.x * TILE, spot0.y * TILE);
    const red = this.reduzido;
    const pose = (i: number, p: Pose) => this.poses.set(m[i]!, p);
    const fala = (i: number, texto: string) => this.falas.set(m[i]!, texto);
    const texto = (extra: string) => {
      for (const id of m) this.textos.set(id, extra ? `${rotulo} · ${extra}` : rotulo);
    };
    if (k === 'videogame') {
      const g = (s.jogo = melhorDe3(s.seed, e));
      this.tvJogo.add(s.mesa.room);
      for (let i = 0; i < 2 && i < m.length; i++) {
        const meu = g.score[i as 0 | 1];
        const dele = g.score[(1 - i) as 0 | 1];
        fala(i, `${g.over && g.winner === i ? '🏆' : '🎮'} ${meu}×${dele}`);
        pose(i, g.over ? (g.winner === i ? 'sitRaise' : 'sit') : red ? 'sit' : 'type');
      }
      texto(`${g.score[0]}×${g.score[1]}`);
    } else if (k === 'pingpong') {
      const p = (s.ping = pingPongDe(s.seed, e));
      const dentro = (e - p.matchStart) % PING_POINT_MS;
      for (let i = 0; i < 2 && i < m.length; i++) {
        const meu = p.score[i as 0 | 1];
        const dele = p.score[(1 - i) as 0 | 1];
        fala(i, `${p.over ? (p.winner === i ? '🏆' : '😩') : '🏓'} ${meu}×${dele}`);
        // quem rebate levanta o braço no começo de cada ponto
        const bate = !p.over && !red && p.points % 2 === i && dentro < BATIDA_MS;
        pose(i, bate || (p.over && p.winner === i) ? 'raise' : 'idle');
      }
      texto(`${p.score[0]}×${p.score[1]}`);
      if (!p.over && p.points !== s.lastPoints && audivel) ctx.toca('pingue-pongue');
      s.lastPoints = p.points;
    } else if (k === 'jokenpo') {
      const j = (s.joken = jokenpoDe(s.seed, e));
      for (let i = 0; i < 2 && i < m.length; i++) {
        const g = j.gestos[i as 0 | 1];
        let texto1 = '✊';
        if (j.fase !== 'pronto') texto1 = GESTOS[g]!;
        if (j.fase === 'resultado') texto1 = j.winner < 0 ? '🤝' : j.winner === i ? `${GESTOS[g]} +🪙` : `${GESTOS[g]} 😵`;
        fala(i, texto1);
        const pulso = j.fase === 'pronto' ? !red && Math.floor(e / 300) % 2 === 0 : j.fase === 'jogou' || j.winner === i;
        pose(i, pulso ? 'raise' : 'idle');
      }
      texto(`${j.score[0]}×${j.score[1]}`);
      if (j.fase === 'jogou' && s.lastFase !== 'jogou' && audivel) ctx.toca('jokenpo');
      s.lastFase = j.fase;
    } else if (k === 'fliperama') {
      fala(0, `🕹️ ${milhar(pontosFliperama(s.seed, e))}`);
      pose(0, !red && Math.floor(e / 400) % 2 === 0 ? 'raise' : 'idle');
      texto('');
      if (audivel && now - s.lastSound > 1500) {
        s.lastSound = now;
        ctx.toca('fliperama');
      }
    } else if (k === 'tv') {
      s.programa = programaDaTv(s.mesa.id, ctx.epoch);
      s.lance = lanceDaTv(s.seed, e);
      const reacao = s.programa === 'futebol' ? t('roda.gol') : s.programa === 'novela' ? '😱' : '😂';
      const pipoca = Math.floor(e / 9000) % m.length;
      for (let i = 0; i < m.length; i++) {
        if (s.lance) fala(i, i === 0 || i === pipoca ? reacao : '');
        else if (i === pipoca && e % 9000 < 3000) fala(i, '🍿');
        if (s.lance && !red) pose(i, 'raise');
      }
      texto(t(`roda.prog.${s.programa}` as Chave));
    } else if (k === 'papo') {
      const v = vezDeFalar(s.seed, m.length, e);
      if (v.visivel) {
        const colega = this.colega(m, sorteio(s.seed, v.turn, Math.max(1, this.nomes.length), 'n'));
        fala(v.speaker, colega && sorteio(s.seed, v.turn, 3) !== 0 ? t(`roda.fofoca.${sorteio(s.seed, v.turn, 4, 'f')}` as Chave, { nome: colega }) : '☕ 😄');
      }
      texto('');
    } else if (k === 'corredor') {
      const v = vezDeFalar(s.seed, m.length, e);
      if (v.visivel) {
        fala(v.speaker, EMOJIS_CORREDOR[sorteio(s.seed, v.turn, EMOJIS_CORREDOR.length, 'e')]!);
        if (!red) pose(v.speaker, 'raise');
      }
      texto('');
    } else {
      const n = Math.floor(e / 3500) + (hashDe(m[0]!) % EMOJIS_ESPELHO.length);
      fala(0, EMOJIS_ESPELHO[n % EMOJIS_ESPELHO.length]!);
      texto('');
    }
  }

  /** Um colega do escritório que não está na roda (só o nome do personagem, nunca texto de sessão). */
  private colega(members: readonly string[], inicio: number): string | undefined {
    const n = this.nomes.length;
    for (let j = 0; j < n; j++) {
      const c = this.nomes[(inicio + j) % n]!;
      if (!members.includes(c.id)) return c.nome;
    }
    return undefined;
  }

  // ---------------------------------------------------------------- desenho

  /** Põe em `out` o que desenhar por cima dos móveis nos andares visíveis. */
  coleta(visiveis: ReadonlySet<string>, out: Drawable[]): void {
    for (const s of this.sessoes) if (s.startedAt !== undefined && visiveis.has(s.floorKey)) for (const d of s.fx) out.push(d);
  }

  private efeitos(s: Viva): Drawable[] {
    const { mesa: m } = s;
    const { x, y } = m.px;
    const spots = m.spots;
    const centro = (i: number) => ({ x: spots[i]!.x * TILE + TILE / 2, y: spots[i]!.y * TILE });
    if (m.kind === 'videogame') {
      return [{ sortY: y - TV_TELA.dy + 28 + 0.1, draw: (c, now) => desenhaTv(c, x, y, 'jogo', now, false, this.reduzido) }];
    }
    if (m.kind === 'tv') {
      const fx: Drawable[] = [
        {
          sortY: y - TV_TELA.dy + 28 + 0.1,
          draw: (c, now) => {
            if (!this.tvJogo.has(m.room)) desenhaTv(c, x, y, s.programa, now, s.lance, this.reduzido);
          },
        },
      ];
      // pipoca: um balde para cada dois espectadores, junto do braço
      spots.forEach((sp, i) => {
        if (i % 2 === 0) fx.push({ sortY: sp.y * TILE + 15, draw: (c, now) => i < s.members.length && desenhaPipoca(c, sp.x * TILE + 11, sp.y * TILE + 1, !this.reduzido && s.lance && Math.floor(now / 200) % 2 === 0) });
      });
      return fx;
    }
    if (m.kind === 'fliperama') return [{ sortY: y - FLIPERAMA_TELA.dy + 30 + 0.1, draw: (c, now) => desenhaFliperamaTela(c, x, y, now, this.reduzido) }];
    if (m.kind === 'pingpong') {
      return [
        {
          sortY: y + 28 + 0.5,
          draw: (c, now) => {
            const p = s.ping;
            if (!p || p.over || this.reduzido || s.startedAt === undefined) return;
            // a bola vai e volta pela mesa a cada ponto, em arco
            const f = (Math.max(0, now - s.startedAt - p.matchStart) % PING_POINT_MS) / PING_POINT_MS;
            const ida = p.points % 2 === 0 ? f : 1 - f;
            desenhaBola(c, x + 3 + Math.round(ida * 40), y + 14, Math.round(Math.sin(f * Math.PI) * 9));
          },
        },
      ];
    }
    if (m.kind === 'jokenpo') {
      return [
        {
          sortY: 1e6,
          draw: (c, now) => {
            const j = s.joken;
            if (!j || j.fase !== 'resultado' || j.winner < 0 || s.startedAt === undefined) return;
            // a moedinha sai de quem perdeu e voa em arco até quem ganhou (parada em cima do vencedor, no movimento reduzido)
            const ganhou = centro(j.winner);
            if (this.reduzido) return desenhaMoeda(c, ganhou.x - 2, ganhou.y - 34, 0);
            const perdeu = centro(1 - j.winner);
            const f = Math.min(1, Math.max(0, (((now - s.startedAt) % JOKENPO_ROUND_MS) - 2000) / 800));
            desenhaMoeda(c, perdeu.x + (ganhou.x - perdeu.x) * f - 2, perdeu.y - 20 + (ganhou.y - perdeu.y) * f - Math.sin(f * Math.PI) * 14, Math.floor(now / 90));
          },
        },
      ];
    }
    if (m.kind === 'espelho') return [{ sortY: 1e6, draw: (c, now) => desenhaBrilho(c, x + 4, y - 3, now + (hashDe(s.seed) % 700), this.reduzido) }];
    return [];
  }
}

/** A instância viva (a do campus), para o painel do agente consultar. */
let atual: Rodas | null = null;

/** O que o agente está fazendo na roda ("📺 TV · Futebol"), ou undefined. */
export function rodaAtual(agentId: string): string | undefined {
  return atual?.textoOf(agentId);
}

/** O traço do agente (14 possíveis), para o painel. */
export function tracoDoAgente(agentId: string): Traco {
  return tracoDe(agentId);
}

/** Nome de personagem para a fofoca: só o do elenco (nunca o título da sessão). */
export function nomeDoElenco(look: string): string | undefined {
  return CAST_BY_ID.get(look)?.name;
}

/**
 * Mantém vivo o "Fazendo agora" do painel: a cada 2 s refaz o texto dos elementos
 * `[data-roda-agente]` com a roda atual do agente (ou o `data-roda-base`, o de antes, quando ele sai da roda).
 */
export function montaPainelRodas(): void {
  if (typeof setInterval !== 'function' || typeof document === 'undefined') return;
  setInterval(() => {
    for (const el of document.querySelectorAll<HTMLElement>('[data-roda-agente]')) {
      const texto = rodaAtual(el.dataset.rodaAgente ?? '') ?? el.dataset.rodaBase ?? '';
      if (el.textContent !== texto) el.textContent = texto;
    }
  }, 2000);
}
